import { AugmentedStandards, Standards } from '../services/backendService/types';

enum SupportedSubjects {
  ela = 'ela',
  math = 'math',
  socialStudies = 'socialStudies',
  science = 'science',
}

enum SupportedStandards {
  TEKS = 'TEKS',
  CCSS = 'CCSS',
}

type SupportedStandardsMetadataKeys = keyof typeof SupportedStandards;

/**
 * This object contains the regular expressions used to parse the strand labels for each supported standard.
 * All regular expressions match only three groups: subject, grade, and code.
 * This objects also optionally contains mappings for the subject and grade groups for each standard.
 *  - The subject group mapping is used to convert the raw subject to a standardized subject.
 *  - The grade group mapping is used to convert the raw grade (or grade range) into an array of standardized grades.
 */
const SupportedStandardsMetadata: Record<
  SupportedStandardsMetadataKeys,
  {
    regex: RegExp;
    subjectGroupMapping?: Record<string, SupportedSubjects>;
    gradeGroupMapping?: Record<SupportedSubjects, Record<string, string>>;
  }
> = {
  TEKS: {
    regex: /TEKS-([A-Za-z]+)-([A-Za-z0-9-]+)\.([A-Za-z0-9]+)/,
    subjectGroupMapping: {
      ELAR: SupportedSubjects.ela,
      SS: SupportedSubjects.socialStudies,
      MATH: SupportedSubjects.math,
      SCIENCE: SupportedSubjects.science,
    },
    gradeGroupMapping: {
      [SupportedSubjects.ela]: {
        // Special mapping for ELA high-school grades
        E1: '9',
        E2: '10',
        E3: '11',
        E4: '12',
        LG: '9-12', // Literary Genres
        AJ: '9-12', // Advanced Journalism
        CW: '10-12', // Creative Writing
      },
      [SupportedSubjects.socialStudies]: {
        // Special mapping for Social Studies high-school grades
        PFE: '9-12', // Personal Financial Literacy
        AA: '9-12', // African American Studies
        MAS: '9-12', // Mexican American Studies
        USG: '9-12', // United States Government
        USH: '9-12', // United States History
        WG: '9-12', // World Geography
        WH: '9-12', // World History
        P: '9-12', // Psychology
        SSAS: '9-12', // Social Studies Advanced Studies
        EC: '9-12', // Economics
        EAS: '9-12', // Economics Advanced Studies
      },
      [SupportedSubjects.science]: {
        // Special mapping for Science high-school grades
        A: '10-12', //Aquatic Science
        AST: '11-12', // Astronomy
        BIO: '9-11', // Biology
        CHEM: '10-12', // Chemistry
        ES: '11-12', // Earth Systems Science
        ENV: '10-12', // Environmental Systems
        IPS: '9-10', // Integrated Physics and Chemistry
        PHYS: '9-12', // Physics
        ST: '10-12', // Special Topics in Science
      },
      [SupportedSubjects.math]: {
        // Special mapping for Math high-school grades
        AII: '10-12', // Algebra II
        AR: '9-12', // Algebraic Reasoning
        DMPS: '9-12', // Discrete Mathematics for Problem Solving
        S: '9-12', // Statistics
        A1: '9-10', // Algebra I
        P: '9-12', // Precalculus
        G: '9-12', // Geometry
        MMA: '9-12', // Mathematical Models with Applications
        AQR: '9-12', // Advanced Quantitative Reasoning
        ISM: '9-12', // Independent Study in Mathematics
      },
    },
  },
  CCSS: {
    regex: /CCSS[\.|-]([A-Za-z]+)-(?:[A-Za-z]+[\.|-][A-Za-z]+)\.([A-Za-z0-9-]+)\.([A-Za-z0-9]+)/,
    subjectGroupMapping: {
      ELA: SupportedSubjects.ela,
    },
  },
};

function convertGradeRangeToArray(gradeRange: string): string[] {
  const formatGrade = (grade: number) => {
    if (grade === 0) return 'Kindergarten';
    if (grade === 1) return `${grade}st`;
    if (grade === 2) return `${grade}nd`;
    if (grade === 3) return `${grade}rd`;
    return `${grade}th`;
  };

  const [start, end] = gradeRange
    .replace('K', '0')
    .split('-')
    .map(Number);

  if (!end) return [formatGrade(start)];
  return Array.from({ length: end - start + 1 }, (_, i) => formatGrade(start + i));
}

function decomposeStrandLabel(
  standardsPrefix: SupportedStandardsMetadataKeys,
  strandLabel: string,
):
  | {
      strandSubject: string;
      strandGrades: string[];
      strandGradeLabel: string;
      strandCode: string;
    }
  | undefined {
  // Get all 3 match groups from the strand label
  const rawStrandLabelParts = strandLabel
    .match(SupportedStandardsMetadata[standardsPrefix].regex)
    ?.slice(1);
  if (!rawStrandLabelParts || rawStrandLabelParts.length !== 3) return undefined;

  // Parse subject from strand label
  const parsedSubject =
    SupportedStandardsMetadata[standardsPrefix].subjectGroupMapping?.[
      rawStrandLabelParts[0]
    ];
  if (!parsedSubject) return undefined;

  // If the grade is alphabetical (e.g., for high-school science), convert it to a valid range first
  const isFullyNumericOrRange = /^\d+$|^\d+-\d+$/.test(rawStrandLabelParts[1]);
  const parsedGradeRange =
    !isFullyNumericOrRange && rawStrandLabelParts[1] !== 'K'
      ? SupportedStandardsMetadata[standardsPrefix].gradeGroupMapping?.[parsedSubject]?.[
          rawStrandLabelParts[1]
        ]
      : rawStrandLabelParts[1];
  if (!parsedGradeRange) return undefined;

  // Convert grade range to an array of standard grades
  const parsedGrades = convertGradeRangeToArray(parsedGradeRange);
  if (!parsedGrades || parsedGradeRange.length === 0) return undefined;

  return {
    strandSubject: parsedSubject,
    strandGrades: parsedGrades,
    strandGradeLabel: rawStrandLabelParts[1],
    strandCode: rawStrandLabelParts[2],
  };
}

/**
 * This function will take in a raw strand label (e.g., CCSS.ELA-LITERACY.RL.6.4) and return
 * its parsed strand parts. Regular expressions are used to selectively extract the subject,
 * grade, and code from the strand label.
 */
export function augmentRawStandard(
  rawStandard: Standards,
): AugmentedStandards | undefined {
  // Extract standard group from strand label (and make sure it's supported)
  const strandPrefix = rawStandard.label.match(/^([A-Za-z]+)[\.|-][A-Za-z0-9-\.]+/)?.[1];
  if (
    !strandPrefix ||
    !Object.values(SupportedStandards).includes(strandPrefix as SupportedStandards)
  ) {
    return undefined;
  }

  // Return parsed strand parts (or undefined if parsing fails)
  const strandLabelParts = decomposeStrandLabel(
    strandPrefix as SupportedStandards,
    rawStandard.label,
  );
  if (!strandLabelParts) {
    return undefined;
  }
  return !strandLabelParts
    ? undefined
    : {
        ...rawStandard,
        subject: strandLabelParts.strandSubject,
        grades: strandLabelParts.strandGrades,
        gradeLabel: strandLabelParts.strandGradeLabel,
        standardsSet: strandPrefix,
        code: strandLabelParts.strandCode,
      };
}

export function sortAugmentedStandardsInplace(
  standards: AugmentedStandards[] | undefined,
) {
  standards?.sort((a, b) => {
    // Compare the gradeLabel alphabetically
    const gradeComparison = a.gradeLabel.localeCompare(b.gradeLabel);
    if (gradeComparison !== 0) {
      return gradeComparison;
    }

    // Extract number and letter from each string
    const [numA, letterA] = a.code.match(/\d+|\D+/g) as [string, string?];
    const [numB, letterB] = b.code.match(/\d+|\D+/g) as [string, string?];

    // Compare the numbers as actual numbers
    const numComparison = Number(numA) - Number(numB);
    if (numComparison !== 0) {
      return numComparison; // If the numbers are different, return the comparison result
    }

    // If the numbers are the same, compare the letters (handle undefined for strings without a letter)
    return (letterA || '').localeCompare(letterB || '');
  });
}
